import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Search, 
  Download, 
  Mail, 
  LogOut,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Loader2,
  UserCheck,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Users,
  Filter,
  SlidersHorizontal,
  X,
  Plus,
  ChevronDown,
  ChevronUp,
  Target,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'EMAIL_VERIFIED_PENDING_ONBOARDING', label: 'Pending Onboarding' },
  { value: 'ONBOARDING_IN_PROGRESS', label: 'In Progress' },
  { value: 'ONBOARDING_COMPLETE', label: 'Completed' }
];

const getStatusBadge = (status) => {
  switch (status) {
    case 'ONBOARDING_COMPLETE':
      return <Badge className="bg-lime-100 text-lime-700 hover:bg-lime-100">Completed</Badge>;
    case 'ONBOARDING_IN_PROGRESS':
      return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">In Progress</Badge>;
    case 'EMAIL_VERIFIED_PENDING_ONBOARDING':
      return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Pending</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export default function AdminCandidates() {
  const { authAxios, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [candidates, setCandidates] = useState([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  
  // Basic Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [page, setPage] = useState(0);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const limit = 20;

  // Advanced Filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Filter type options
  const filterTypeOptions = [
    { value: 'function', label: 'Function Interest', key: 'by_function' },
    { value: 'industry', label: 'Industry Interest', key: 'by_industry' },
    { value: 'geography', label: 'Geography', key: 'by_geography' },
    { value: 'experience', label: 'Experience Level', key: 'by_experience' },
    { value: 'company_size', label: 'Company Size', key: 'by_company_size' },
  ];

  // Fetch analytics data for filter dropdowns
  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!authAxios) return;
      setLoadingAnalytics(true);
      try {
        const response = await authAxios.get('/admin/dashboard/analytics');
        setAnalyticsData(response.data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setLoadingAnalytics(false);
      }
    };
    fetchAnalytics();
  }, [authAxios]);

  // Get value options for a filter type
  const getFilterValueOptions = (filterType) => {
    if (!analyticsData) return [];
    const config = filterTypeOptions.find(f => f.value === filterType);
    if (!config) return [];
    const data = analyticsData[config.key] || [];
    return data.map(item => ({ name: item.name, count: item.count }));
  };

  // Add a new filter
  const addAdvancedFilter = () => {
    const usedTypes = advancedFilters.map(f => f.type);
    const availableType = filterTypeOptions.find(f => !usedTypes.includes(f.value));
    if (availableType) {
      setAdvancedFilters([...advancedFilters, {
        id: Date.now(),
        type: availableType.value,
        value: ''
      }]);
    }
  };

  // Remove a filter
  const removeAdvancedFilter = (filterId) => {
    setAdvancedFilters(advancedFilters.filter(f => f.id !== filterId));
  };

  // Update a filter
  const updateAdvancedFilter = (filterId, field, value) => {
    setAdvancedFilters(advancedFilters.map(f => {
      if (f.id === filterId) {
        if (field === 'type') {
          return { ...f, type: value, value: '' };
        }
        return { ...f, [field]: value };
      }
      return f;
    }));
  };

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setAdvancedFilters([]);
    setPage(0);
  };

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit
      });
      
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      // Add advanced filters
      const activeFilters = advancedFilters.filter(f => f.type && f.value);
      if (activeFilters.length > 0) {
        const criteria = activeFilters.reduce((acc, f) => {
          acc[f.type] = f.value;
          return acc;
        }, {});
        params.append('segment_criteria', JSON.stringify(criteria));
      }
      
      const response = await authAxios.get(`/admin/candidates?${params.toString()}`);
      setCandidates(response.data.candidates || []);
      setTotalCandidates(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [authAxios, page, statusFilter, searchQuery, advancedFilters]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  useEffect(() => {
    // Update URL params when filter changes
    if (statusFilter && statusFilter !== 'all') {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    loadCandidates();
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setPage(0);
    setSelectedCandidates([]);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedCandidates(candidates.map(c => c.id));
    } else {
      setSelectedCandidates([]);
    }
  };

  const handleSelectCandidate = (candidateId, checked) => {
    if (checked) {
      setSelectedCandidates(prev => [...prev, candidateId]);
    } else {
      setSelectedCandidates(prev => prev.filter(id => id !== candidateId));
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      params.append('format', 'csv');
      
      // Include advanced filters in export
      const activeFilters = advancedFilters.filter(f => f.type && f.value);
      if (activeFilters.length > 0) {
        const criteria = activeFilters.reduce((acc, f) => {
          acc[f.type] = f.value;
          return acc;
        }, {});
        params.append('segment_criteria', JSON.stringify(criteria));
      }
      
      const response = await authAxios.get(`/admin/exports/candidates?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidates_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleSendReminders = async () => {
    if (selectedCandidates.length === 0) {
      toast.error('Please select candidates first');
      return;
    }
    
    setSendingEmails(true);
    try {
      const response = await authAxios.post('/admin/candidates/send-reminders', {
        candidate_ids: selectedCandidates
      });
      
      toast.success(`Reminder emails sent to ${response.data.sent_count} candidates`);
      setShowEmailDialog(false);
      setSelectedCandidates([]);
      loadCandidates();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reminders';
      toast.error(message);
    } finally {
      setSendingEmails(false);
    }
  };

  const totalPages = Math.ceil(totalCandidates / limit);
  const incompleteSelected = selectedCandidates.filter(id => {
    const candidate = candidates.find(c => c.id === id);
    return candidate && candidate.candidate_status !== 'ONBOARDING_COMPLETE';
  }).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-6">
              <Link to="/" className="text-xl font-heading text-slate-900">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                <Link to="/admin" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Overview
                </Link>
                <Link to="/admin/candidates" className="px-4 py-2 text-sm font-medium text-lime-700 bg-lime-50 rounded-lg">
                  Candidates
                </Link>
                <Link to="/admin/job-analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Market
                </Link>
                <Link to="/admin/analytics" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Analytics
                </Link>
                <Link to="/admin/market-pulse" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Market Pulse
                </Link>
                <Link to="/admin/messages" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Messages
                </Link>
                <Link to="/admin/support" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  User Support
                </Link>
                <Link to="/admin/job-import" className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg">
                  Job Import
                </Link>
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/admin/guide">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Admin Guide
                </Button>
              </Link>
              <Link to="/brand" target="_blank">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600">
                  Brand Guide
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-heading text-slate-900">Candidate Management</h1>
            <p className="text-slate-500">View, filter, and manage all candidates</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export CSV
            </Button>
            <Button
              onClick={() => setShowEmailDialog(true)}
              disabled={selectedCandidates.length === 0}
              className="bg-evergreen-950 hover:bg-evergreen-900"
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Reminders ({selectedCandidates.length})
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </form>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <Select value={statusFilter} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  variant={showAdvancedFilters || advancedFilters.length > 0 ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  className={advancedFilters.length > 0 ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Advanced
                  {advancedFilters.length > 0 && (
                    <Badge className="ml-2 bg-white text-indigo-600">{advancedFilters.length}</Badge>
                  )}
                  {showAdvancedFilters ? (
                    <ChevronUp className="h-4 w-4 ml-1" />
                  ) : (
                    <ChevronDown className="h-4 w-4 ml-1" />
                  )}
                </Button>
                <Button variant="ghost" size="icon" onClick={loadCandidates}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="mt-4 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-indigo-600" />
                    <Label className="text-sm font-medium text-indigo-700">Advanced Segment Filters</Label>
                    <span className="text-xs text-indigo-500">(AND logic - matches ALL criteria)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {advancedFilters.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAdvancedFilters}
                        className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100"
                      >
                        Clear All
                      </Button>
                    )}
                    {advancedFilters.length < filterTypeOptions.length && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={addAdvancedFilter}
                        className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Filter
                      </Button>
                    )}
                  </div>
                </div>

                {loadingAnalytics ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
                    <span className="ml-2 text-sm text-indigo-600">Loading filter options...</span>
                  </div>
                ) : advancedFilters.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-indigo-600 mb-2">
                      Add filters to find candidates by function, industry, geography, and more.
                    </p>
                    <p className="text-xs text-indigo-500">
                      Example: Find all VP-level candidates interested in Sales roles in US East
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {advancedFilters.map((filter, index) => {
                      const usedTypes = advancedFilters.filter(f => f.id !== filter.id).map(f => f.type);
                      const availableTypes = filterTypeOptions.filter(f => !usedTypes.includes(f.value) || f.value === filter.type);
                      const valueOptions = getFilterValueOptions(filter.type);

                      return (
                        <div key={filter.id} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-indigo-100">
                          {index > 0 && (
                            <span className="text-xs font-medium text-indigo-500 bg-indigo-100 px-2 py-1 rounded mt-1">AND</span>
                          )}
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-slate-500">Filter By</Label>
                              <Select
                                value={filter.type}
                                onValueChange={(val) => updateAdvancedFilter(filter.id, 'type', val)}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {availableTypes.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-slate-500">Value</Label>
                              {valueOptions.length > 0 ? (
                                <Select
                                  value={filter.value}
                                  onValueChange={(val) => updateAdvancedFilter(filter.id, 'value', val)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select value" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {valueOptions.map(opt => (
                                      <SelectItem key={opt.name} value={opt.name}>
                                        {opt.name} ({opt.count})
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  className="h-9"
                                  placeholder={filter.type ? "Enter value" : "Select type first"}
                                  value={filter.value}
                                  onChange={(e) => updateAdvancedFilter(filter.id, 'value', e.target.value)}
                                  disabled={!filter.type}
                                />
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAdvancedFilter(filter.id)}
                            className="text-slate-400 hover:text-red-500 mt-5"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Filter Summary */}
                {advancedFilters.length > 0 && advancedFilters.every(f => f.type && f.value) && (
                  <div className="mt-3 p-3 bg-indigo-100 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-800 flex items-center gap-1">
                          <Target className="h-4 w-4" />
                          Finding candidates matching:
                        </p>
                        <div className="text-sm text-indigo-700 mt-1">
                          {advancedFilters.map((f, i) => (
                            <span key={f.id}>
                              {i > 0 && <span className="font-medium"> AND </span>}
                              <span className="font-medium">{filterTypeOptions.find(o => o.value === f.type)?.label}</span>
                              {' = '}
                              <span className="font-semibold">&quot;{f.value}&quot;</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={loadCandidates}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500">
              Showing {candidates.length} of {totalCandidates} candidates
            </span>
            {selectedCandidates.length > 0 && (
              <Badge variant="secondary">
                {selectedCandidates.length} selected
              </Badge>
            )}
          </div>
        </div>

        {/* Candidates Table */}
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={candidates.length > 0 && selectedCandidates.length === candidates.length}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No candidates found
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates.map((candidate) => (
                    <TableRow key={candidate.id} className="hover:bg-slate-50">
                      <TableCell>
                        <Checkbox
                          checked={selectedCandidates.includes(candidate.id)}
                          onCheckedChange={(checked) => handleSelectCandidate(candidate.id, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">
                            {candidate.profile?.first_name && candidate.profile?.last_name
                              ? `${candidate.profile.first_name} ${candidate.profile.last_name}`
                              : candidate.email.split('@')[0]}
                          </p>
                          <p className="text-sm text-slate-500">{candidate.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(candidate.candidate_status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-lime-500 rounded-full"
                              style={{ width: `${candidate.onboarding_completion_percent || 0}%` }}
                            />
                          </div>
                          <span className="text-sm text-slate-600">
                            {candidate.onboarding_completion_percent || 0}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {candidate.created_at 
                          ? new Date(candidate.created_at).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {candidate.last_login_at 
                          ? new Date(candidate.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/admin/candidates/${candidate.id}`}>
                            View
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
        </Card>
      </main>

      {/* Email Reminder Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Profile Completion Reminders</DialogTitle>
            <DialogDescription>
              Send an email reminder to selected candidates to complete their profile.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Mail className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  {selectedCandidates.length} candidates selected
                </p>
                <p className="text-sm text-slate-500">
                  {incompleteSelected} have incomplete profiles
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-600">
              This will send an email to all selected candidates reminding them to complete their profile setup.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendReminders}
              disabled={sendingEmails || incompleteSelected === 0}
              className="bg-evergreen-950 hover:bg-evergreen-900"
            >
              {sendingEmails ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send Reminders
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
